import { notification } from "antd";
import cuid from "cuid";
import { Formik } from "formik";
import { get } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAdd,
  MdCheck,
  MdCheckCircle,
  MdContentCopy,
  MdErrorOutline,
  MdLink,
  MdOutlineDelete,
  MdOutlineDescription,
  MdPeople,
  MdRocketLaunch,
  MdUpload,
} from "react-icons/md";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import graphql from "utils/api/graphql";
import { DISTRIBUTIONS } from "utils/api/graphql/queries/distributions";
import {
  CONTENT_TYPES,
  INSURANCE_PLAN_ATTACHMENTS_TYPES,
  ORGANIZATION_TYPES,
  USER_TYPES,
} from "utils/constants";
import { bindInputProps } from "utils/helpers/input";
import * as yup from "yup";
import CSVMappingModal from "./CSVMappingModal";

const employeeInitialValues = {
  email: "",
  firstName: "",
  lastName: "",
};

const getEmployeeValidationSchema = (messages) =>
  yup.object().shape({
    email: yup
      .string()
      .trim()
      .email(messages.email)
      .required(messages.required),
    firstName: yup
      .string()
      .trim()
      .max(100, messages.firstNameMax)
      .required(messages.required),
    lastName: yup
      .string()
      .trim()
      .max(100, messages.lastNameMax)
      .required(messages.required),
  });

export default function DeploymentManager({ enterpriseId }) {
  const [deployments, setDeployments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [showCSVMapping, setShowCSVMapping] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const fileInputRef = useRef(null);
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.COMPANY);
  const { t: translateErrors } = useTranslation(NAME_SPACES.ERRORS);
  const DEPLOYMENT_TEXT =
    t("DEPLOYMENT_MANAGER", { returnObjects: true }) || {};
  const {
    ALERT = {},
    HERO: HERO_TEXT = {},
    EMPLOYEE_SECTION: EMPLOYEE_SECTION_TEXT = {},
    CSV_HELP = {},
    FORM: FORM_TEXT = {},
    TABLE: TABLE_TEXT = {},
    NOTIFICATIONS = {},
  } = DEPLOYMENT_TEXT;
  const HERO = {
    ...HERO_TEXT,
    PUBLIC_LINK: HERO_TEXT?.PUBLIC_LINK || {},
    TARGETED_INVITES: HERO_TEXT?.TARGETED_INVITES || {},
  };
  const EMPLOYEE_SECTION = {
    ...EMPLOYEE_SECTION_TEXT,
    GUIDE: {
      STEPS: [],
      ...(EMPLOYEE_SECTION_TEXT?.GUIDE || {}),
    },
    SUMMARY: {
      ...(EMPLOYEE_SECTION_TEXT?.SUMMARY || {}),
    },
    IMPORT_BUTTON: {
      ...(EMPLOYEE_SECTION_TEXT?.IMPORT_BUTTON || {}),
    },
  };
  const emailValidationMessage = translateErrors("VALIDATIONS.EMAIL");
  const requiredValidationMessage = translateErrors("VALIDATIONS.REQUIRED");
  const maxLengthValidationMessage = translateErrors("VALIDATIONS.MAX_LENGTH", {
    length: 100,
  });
  const employeeValidationSchema = useMemo(
    () =>
      getEmployeeValidationSchema({
        email: emailValidationMessage,
        required: requiredValidationMessage,
        firstNameMax: maxLengthValidationMessage,
        lastNameMax: maxLengthValidationMessage,
      }),
    [
      emailValidationMessage,
      maxLengthValidationMessage,
      requiredValidationMessage,
    ],
  );
  const genericApiError = translateErrors("API.GENERIC");

  const {
    data: organizationData,
    loading: organizationLoading,
    refetch: refetchOrganization,
  } = useQuery(graphql.queries.ORGANIZATION, {
    skip: !enterpriseId,
    variables: {
      where: { id: enterpriseId },
    },
    fetchPolicy: "network-only",
  });

  const organizationType = get(organizationData, "organization.type");
  const isCDGOrganization = organizationType === ORGANIZATION_TYPES.CDG;

  const {
    data: organizationBranchesData,
    loading: branchesLoading,
    refetch: refetchBranches,
  } = useQuery(graphql.queries.ORGANIZATIONS, {
    skip: !enterpriseId || !isCDGOrganization,
    variables: {
      where: {
        counterpartyContracts: {
          organization: {
            id: enterpriseId,
          },
        },
      },
    },
    fetchPolicy: "network-only",
  });

  const {
    data: noticeData,
    loading: noticeLoading,
    refetch: refetchNotice,
  } = useQuery(graphql.queries.ATTACHMENTS, {
    skip: !enterpriseId,
    variables: {
      where: {
        organization: { id: enterpriseId },
      },
      isIn: {
        type: [INSURANCE_PLAN_ATTACHMENTS_TYPES.INFO_NOTICE],
      },
    },
    fetchPolicy: "network-only",
  });

  const {
    data: distributionsData,
    loading: distributionsLoading,
    refetch: refetchDistributions,
  } = useQuery(DISTRIBUTIONS, {
    skip: !enterpriseId,
    variables: {
      where: {
        broker: {
          organization: {
            id: enterpriseId,
          },
        },
      },
      orderBy: [{ createdDate: "DESC" }],
    },
    fetchPolicy: "network-only",
  });

  const {
    data: employeesData,
    loading: employeesLoading,
    refetch: refetchEmployees,
  } = useQuery(graphql.queries.USERS, {
    skip: !enterpriseId,
    variables: {
      where: {
        organization: { id: enterpriseId },
        meta: { type: USER_TYPES.CONTRACT_GUEST.type },
        isActive: false,
      },
      take: null,
    },
    fetchPolicy: "network-only",
  });
  const [createUsers] = useMutation(graphql.mutations.CREATE_USERS);
  const [deleteUser] = useMutation(graphql.mutations.DELETE_USER);
  const [addDomain] = useMutation(graphql.mutations.DOMAIN_SIGN_UP);
  const [deployProjectsForUsers] = useMutation(
    graphql.mutations.DEPLOY_PROJECTS_FOR_USERS,
  );
  const [importUsersFromFile] = useMutation(
    graphql.mutations.IMPORT_USERS_FROM_FILE,
  );

  const noticeAttachment = useMemo(
    () => get(noticeData, "attachments.data[0]", null),
    [noticeData],
  );

  const selectedProducts = useMemo(() => {
    const items = get(distributionsData, "distributions.data", []) || [];
    return items
      .map((distribution) => get(distribution, "insurancePlan"))
      .filter(Boolean);
  }, [distributionsData]);

  const contract = useMemo(
    () =>
      get(organizationData, "organization.counterpartyContracts[0]", null) ||
      null,
    [organizationData],
  );

  const contractConfig = useMemo(() => {
    if (!contract?.id) {
      return null;
    }

    return {
      id: contract.id,
      processId: get(contract, "subjects.processId", null),
      hasNotice: Boolean(noticeAttachment),
      products: selectedProducts,
    };
  }, [contract, selectedProducts, noticeAttachment]);

  const processId = contractConfig?.processId;

  const {
    data: sourceData,
    loading: sourceLoading,
    refetch: refetchSource,
  } = useQuery(graphql.queries.SOURCE, {
    skip: !processId,
    variables: {
      where: {
        tracers: {
          flow: {
            process: {
              id: processId,
            },
          },
        },
      },
    },
    fetchPolicy: "network-only",
  });

  const isConfigComplete = useMemo(() => {
    if (!contractConfig) return false;
    return (
      Boolean(contractConfig.processId) &&
      contractConfig.hasNotice &&
      contractConfig.products.length > 0
    );
  }, [contractConfig]);

  const organizationBranches = useMemo(() => {
    if (!isCDGOrganization) return [];
    return get(organizationBranchesData, "organizations.data", []) || [];
  }, [organizationBranchesData, isCDGOrganization]);
  const hasBranchRequirement =
    !isCDGOrganization || organizationBranches.length > 0;
  const sourceId = get(sourceData, "source.id");
  const shouldFetchSource = Boolean(processId);

  const reloadData = useCallback(async () => {
    setLoading(true);
    try {
      const refetchers = [
        refetchOrganization?.(),
        refetchNotice?.(),
        refetchDistributions?.(),
        refetchEmployees?.(),
      ];

      if (isCDGOrganization) {
        refetchers.push(refetchBranches?.());
      }

      if (shouldFetchSource && refetchSource) {
        refetchers.push(refetchSource?.());
      }

      await Promise.all(refetchers.filter(Boolean));
    } catch (error) {
      console.error("Error loading deployment prerequisites:", error);
    } finally {
      setLoading(false);
    }
  }, [
    refetchDistributions,
    refetchEmployees,
    refetchNotice,
    refetchOrganization,
    refetchBranches,
    isCDGOrganization,
    refetchSource,
    shouldFetchSource,
  ]);

  useEffect(() => {
    if (enterpriseId) {
      reloadData();
    }
  }, [enterpriseId, reloadData]);

  useEffect(() => {
    const items = get(employeesData, "users.data", []) || [];
    setEmployees(
      items.map((user) => ({
        id: user.id,
        email: user.email,
        firstName: user.firstname || "",
        lastName: user.lastname || "",
      })),
    );
  }, [employeesData]);

  async function handleAddEmployee(values, { resetForm, setSubmitting }) {
    const trimmedEmail = values.email?.trim();
    if (!trimmedEmail) return;
    setAdding(true);
    try {
      const response = await createUsers({
        variables: {
          data: [
            {
              id: cuid(),
              email: trimmedEmail,
              firstname: values.firstName?.trim() || "",
              lastname: values.lastName?.trim() || "",
              organization: { id: enterpriseId },
              meta: { type: USER_TYPES.CONTRACT_GUEST.type },
              isActive: false,
            },
          ],
        },
      });

      const createdUsers = get(response, "data.addUsers", []);
      if (!createdUsers?.length) {
        resetForm();
        notification.warning({
          message:
            NOTIFICATIONS.EMPLOYEE_ALREADY_EXISTS ||
            NOTIFICATIONS.EMPLOYEE_ADDED_ERROR,
        });
        return;
      }

      resetForm();
      await reloadData();
      notification.success({
        message: NOTIFICATIONS.EMPLOYEE_ADDED_SUCCESS,
      });
    } catch (error) {
      console.error("handleAddEmployee error", error);
      notification.error({
        message: NOTIFICATIONS.EMPLOYEE_ADDED_ERROR,
        description: genericApiError,
      });
    } finally {
      setAdding(false);
      setSubmitting(false);
    }
  }

  async function handleDelete(id) {
    if (!id) return;

    try {
      await deleteUser({
        variables: {
          where: { id },
        },
      });
      await reloadData();
      notification.success({
        message:
          NOTIFICATIONS.EMPLOYEE_REMOVED_SUCCESS ||
          NOTIFICATIONS.EMPLOYEE_ADDED_SUCCESS,
      });
    } catch (error) {
      console.error("handleDelete error", error);
      notification.error({
        message:
          NOTIFICATIONS.EMPLOYEE_REMOVED_ERROR ||
          NOTIFICATIONS.EMPLOYEE_ADDED_ERROR,
        description: genericApiError,
      });
    }
  }

  function handleFileChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      CONTENT_TYPES.CSV,
      CONTENT_TYPES.XLSX,
      CONTENT_TYPES.XLS,
      CONTENT_TYPES.SPREADSHEET,
    ];
    const fileType = file.type || "";
    const fileName = file.name.toLowerCase();
    const isValidType =
      validTypes.includes(fileType) ||
      fileName.endsWith(".csv") ||
      fileName.endsWith(".xlsx") ||
      fileName.endsWith(".xls");

    if (!isValidType) {
      notification.error({
        message:
          t("DEPLOYMENT_MANAGER.EMPLOYEE_SECTION.INVALID_FILE_TYPE") ||
          "Invalid file type",
        description:
          t("DEPLOYMENT_MANAGER.EMPLOYEE_SECTION.INVALID_FILE_TYPE_DESC") ||
          "Please select a CSV, XLS, or XLSX file",
      });
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Check if it's an Excel file
    const isExcel = fileName.endsWith(".xlsx") || fileName.endsWith(".xls");

    if (isExcel) {
      // Use backend import mutation for Excel files
      handleExcelImport(file);
    } else {
      // Use CSV mapping modal popup for CSV files
      setCsvFile(file);
      setShowCSVMapping(true);
    }

    // Reset file input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  async function handleExcelImport(file) {
    setImporting(true);

    try {
      // Convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const { data, errors } = await importUsersFromFile({
        variables: {
          data: {
            base64,
            userData: {
              organization: { id: enterpriseId },
              meta: { type: USER_TYPES.CONTRACT_GUEST.type },
              isActive: false,
            },
          },
        },
      });

      if (!errors) {
        const count = data?.importUsersFromFile?.length || 0;
        notification.success({
          message: NOTIFICATIONS.IMPORT.SUCCESS_TITLE,
          description:
            t("DEPLOYMENT_MANAGER.NOTIFICATIONS.IMPORT.SUCCESS_DESC", {
              imported: count,
              skipped: 0,
            }) || `${count} employee(s) imported successfully`,
        });
        await reloadData();
      } else {
        notification.error({
          message: NOTIFICATIONS.IMPORT.ERROR_TITLE,
          description: genericApiError,
        });
      }
    } catch (error) {
      console.error("Error importing Excel file:", error);
      const errorMessage = error?.message || "";

      if (errorMessage === "USER_ALREADY_EXISTS") {
        notification.warning({
          message: NOTIFICATIONS.IMPORT.INTERRUPTED_TITLE,
          description: NOTIFICATIONS.IMPORT.INTERRUPTED_DESCRIPTION,
        });
      } else {
        notification.error({
          message: NOTIFICATIONS.IMPORT.ERROR_TITLE,
          description: genericApiError,
        });
      }
    } finally {
      setImporting(false);
    }
  }

  function handleFileButtonClick() {
    fileInputRef.current?.click();
  }

  async function handleCSVImport(employeesToImport) {
    setImporting(true);
    setShowCSVMapping(false);

    const existingEmails = employees.map((e) => e.email.toLowerCase());
    const newEmployees = employeesToImport
      .filter((emp) => !existingEmails.includes(emp.email.toLowerCase()))
      .map((emp) => ({
        email: emp.email,
        firstName: emp.firstName,
        lastName: emp.lastName,
      }));

    if (newEmployees.length === 0) {
      notification.info({
        message: NOTIFICATIONS.IMPORT.NONE_TITLE,
        description: NOTIFICATIONS.IMPORT.NONE_DESCRIPTION,
      });
      setImporting(false);
      return;
    }

    try {
      await createUsers({
        variables: {
          data: newEmployees.map((employee) => ({
            id: cuid(),
            email: employee.email,
            firstname: employee.firstName || "",
            lastname: employee.lastName || "",
            organization: { id: enterpriseId },
            meta: { type: USER_TYPES.CONTRACT_GUEST.type },
          })),
        },
      });

      const skipped = employeesToImport.length - newEmployees.length;
      notification.success({
        message: NOTIFICATIONS.IMPORT.SUCCESS_TITLE,
        description: t(
          skipped > 0
            ? "DEPLOYMENT_MANAGER.NOTIFICATIONS.IMPORT.SUCCESS_WITH_SKIPPED_DESC"
            : "DEPLOYMENT_MANAGER.NOTIFICATIONS.IMPORT.SUCCESS_DESC",
          {
            imported: newEmployees.length,
            skipped,
          },
        ),
      });
      await reloadData();
    } catch (error) {
      const errorMessage = error?.message || "";

      if (errorMessage === "USER_ALREADY_EXISTS") {
        notification.warning({
          message: NOTIFICATIONS.IMPORT.INTERRUPTED_TITLE,
          description: NOTIFICATIONS.IMPORT.INTERRUPTED_DESCRIPTION,
        });
      } else {
        console.error("handleCSVImport error", error);
        notification.error({
          message: NOTIFICATIONS.IMPORT.ERROR_TITLE,
          description: genericApiError,
        });
      }
    } finally {
      setImporting(false);
    }
  }

  async function handleDeployPublicLink() {
    if (!contractConfig?.id || !isConfigComplete) {
      notification.warning({
        message: NOTIFICATIONS.CONFIG.INCOMPLETE_TITLE,
        description: NOTIFICATIONS.CONFIG.LINK_DESC,
      });
      return;
    }

    if (!hasBranchRequirement) {
      notification.warning({
        message:
          NOTIFICATIONS?.BRANCHES?.TITLE ||
          NOTIFICATIONS.CONFIG.INCOMPLETE_TITLE,
        description:
          NOTIFICATIONS?.BRANCHES?.DESCRIPTION ||
          NOTIFICATIONS.CONFIG.LINK_DESC,
      });
      return;
    }

    if (!sourceId) {
      notification.warning({
        message: NOTIFICATIONS.PUBLIC_LINK_ERROR,
        description: genericApiError,
      });
      return;
    }

    setDeploying(true);

    const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
    const publicLinkUrl = `${baseUrl}/#/public/process/init/${sourceId}?cuid=${contractConfig.id}`;

    try {
      await addDomain({
        variables: {
          data: {
            id: contractConfig.id,
            name: publicLinkUrl,
            meta: {
              type: "guest.contract",
            },
            organization: {
              id: enterpriseId,
            },
          },
        },
      });

      setDeployments((previous) => [
        {
          id: contractConfig.id,
          deployment_type: "link",
          public_link_url: publicLinkUrl,
        },
        ...previous.filter(
          (deployment) => deployment.deployment_type !== "link",
        ),
      ]);

      await reloadData();
      notification.success({
        message: NOTIFICATIONS.PUBLIC_LINK_SUCCESS,
      });
    } catch (error) {
      console.error("Error while creating the public link", error);
      notification.error({
        message: NOTIFICATIONS.PUBLIC_LINK_ERROR,
        description: genericApiError,
      });
    } finally {
      setDeploying(false);
    }
  }

  async function handleDeployToList() {
    if (!contractConfig?.id || !isConfigComplete) {
      notification.warning({
        message: NOTIFICATIONS.CONFIG.INCOMPLETE_TITLE,
        description: NOTIFICATIONS.CONFIG.DEPLOY_DESC,
      });
      return;
    }

    if (!hasBranchRequirement) {
      notification.warning({
        message:
          NOTIFICATIONS?.BRANCHES?.TITLE ||
          NOTIFICATIONS.CONFIG.INCOMPLETE_TITLE,
        description:
          NOTIFICATIONS?.BRANCHES?.DESCRIPTION ||
          NOTIFICATIONS.CONFIG.DEPLOY_DESC,
      });
      return;
    }

    if (!sourceId) {
      notification.warning({
        message: NOTIFICATIONS.PUBLIC_LINK_ERROR,
        description: genericApiError,
      });
      return;
    }

    if (employees.length === 0) {
      notification.info({
        message: NOTIFICATIONS.NO_EMPLOYEES.TITLE,
        description: NOTIFICATIONS.NO_EMPLOYEES.DESCRIPTION,
      });
      return;
    }

    setDeploying(true);

    try {
      await deployProjectsForUsers({
        variables: {
          data: {
            usersFilter: {
              meta: { type: USER_TYPES.CONTRACT_GUEST.type },
              organization: {
                id: enterpriseId,
              },
            },
            source: {
              id: sourceId,
            },
            organization: {
              id: enterpriseId,
            },
            baseUrl: process.env.REACT_APP_BASE_URL,
          },
        },
      });

      await reloadData();
      notification.success({
        message: NOTIFICATIONS.DEPLOY_SUCCESS_TITLE,
        description: t("DEPLOYMENT_MANAGER.NOTIFICATIONS.DEPLOY_SUCCESS_DESC", {
          count: employees.length,
        }),
      });
    } catch (error) {
      console.error("Error deploying targeted invites:", error);
      notification.error({
        message: NOTIFICATIONS.DEPLOY_ERROR,
        description: genericApiError,
      });
    } finally {
      setDeploying(false);
    }
  }

  function getDeploymentLink(deployment) {
    if (!deployment) return null;
    if (deployment.public_link_url) return deployment.public_link_url;
    if (deployment.public_link_token) {
      return `${window.location.origin}/enroll/${deployment.public_link_token}`;
    }
    return null;
  }

  function copyLink(deployment) {
    const publicUrl = getDeploymentLink(deployment);
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    setCopiedLinkId(deployment.id);
    setTimeout(() => setCopiedLinkId(null), 2000);
  }

  const linkDeployments = deployments.filter(
    (d) => d.deployment_type === "link",
  );
  const hasPublicLink = linkDeployments.length > 0;

  const isLoading =
    loading ||
    organizationLoading ||
    noticeLoading ||
    distributionsLoading ||
    employeesLoading ||
    (isCDGOrganization && branchesLoading) ||
    (processId && sourceLoading);
  const showConfigReminder = !isConfigComplete;

  if (isLoading) {
    return (
      <div className="company__flex company__items-center company__justify-center company__py-12">
        <div className="company__loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="company__space-y-8">
      {showConfigReminder && (
        <div className="company__bg-amber-50 company__border company__border-amber-200 company__text-amber-900 company__rounded-lg company__p-4">
          <div className="company__flex company__gap-3">
            <MdErrorOutline
              className="company__w-5 company__h-5 company__flex-shrink-0"
              style={{ marginTop: "0.125rem" }}
            />
            <div className="company__text-sm">
              <p className="company__font-semibold">{ALERT.TITLE}</p>
              <p>{ALERT.DESCRIPTION}</p>
            </div>
          </div>
        </div>
      )}
      <div className="company__bg-gradient-to-br company__from-slate-50 company__to-blue-50 company__rounded-xl company__border-2 company__border-slate-200 company__p-8">
        <div className="company__flex company__items-start company__gap-6">
          <div className="company__bg-blue-600 company__rounded-full company__p-4">
            <MdRocketLaunch className="company__w-8 company__h-8 company__text-white" />
          </div>
          <div className="company__flex-1">
            <h2 className="company__text-2xl company__font-bold company__text-slate-900 company__mb-3">
              {HERO.TITLE}
            </h2>
            <p className="company__text-slate-700 company__text-lg company__mb-6">
              {HERO.SUBTITLE}
            </p>

            <div className="company__grid company__md-grid-cols-2 company__gap-6">
              <div className="company__bg-white company__rounded-xl company__p-6 company__border-2 company__border-slate-200 company__shadow-sm">
                <div className="company__flex company__items-center company__gap-3 company__mb-4">
                  <div className="company__bg-blue-100 company__rounded-lg company__p-3">
                    <MdLink className="company__w-6 company__h-6 company__text-blue-600" />
                  </div>
                  <div>
                    <h3 className="company__font-semibold company__text-lg company__text-slate-900">
                      {HERO.PUBLIC_LINK.TITLE}
                    </h3>
                    <p className="company__text-sm company__text-slate-600">
                      {HERO.PUBLIC_LINK.SUBTITLE}
                    </p>
                  </div>
                </div>

                <p className="company__text-sm company__text-slate-700 company__mb-4">
                  {HERO.PUBLIC_LINK.DESCRIPTION}
                </p>

                <div className="company__flex company__items-center company__gap-2 company__text-xs company__text-slate-600 company__mb-4">
                  <MdCheck className="company__w-4 company__h-4 company__text-green-600" />
                  <span>{HERO.PUBLIC_LINK.BULLET}</span>
                </div>

                {hasPublicLink ? (
                  <div className="company__space-y-3">
                    <div className="company__bg-green-50 company__border company__border-green-200 company__rounded-lg company__p-4">
                      <p className="company__text-sm company__font-medium company__text-green-900 company__mb-2">
                        {HERO.PUBLIC_LINK.ACTIVE_LABEL}
                      </p>
                      <div className="company__flex company__gap-2">
                        <Button
                          onClick={() => copyLink(linkDeployments[0])}
                          type="success--light"
                        >
                          {copiedLinkId === linkDeployments[0].id ? (
                            <>
                              <MdCheckCircle size={16} />
                              {HERO.PUBLIC_LINK.COPY.SUCCESS}
                            </>
                          ) : (
                            <>
                              <MdContentCopy size={16} />
                              {HERO.PUBLIC_LINK.COPY.DEFAULT}
                            </>
                          )}
                        </Button>
                        {/* <button
                          onClick={() => openPublicLink(linkDeployments[0])}
                          className="company__flex company__items-center company__gap-2 company__px-4 company__py-2 company__bg-white company__border company__border-green-300 company__text-green-700 company__rounded-lg company__hover-bg-green-50 company__transition-colors company__text-sm company__font-medium"
                        >
                          <ExternalLink size={16} />
                          {HERO.PUBLIC_LINK.TEST}
                        </button> */}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Button
                    onClick={handleDeployPublicLink}
                    disabled={
                      deploying || !isConfigComplete || !hasBranchRequirement
                    }
                    type="primary"
                    className="company__w-full"
                  >
                    <MdLink className="company__w-5 company__h-5" />
                    {deploying
                      ? HERO.PUBLIC_LINK.BUTTON.LOADING
                      : !isConfigComplete
                        ? HERO.PUBLIC_LINK.BUTTON.DISABLED
                        : !hasBranchRequirement
                          ? HERO.PUBLIC_LINK.BUTTON.BRANCHES_REQUIRED
                          : HERO.PUBLIC_LINK.BUTTON.DEFAULT}
                  </Button>
                )}
                {isCDGOrganization && !hasBranchRequirement && (
                  <p className="company__mt-3 company__text-xs company__text-amber-600">
                    {HERO.BRANCHES_REQUIRED}
                  </p>
                )}
              </div>

              <div className="company__bg-white company__rounded-xl company__p-6 company__border-2 company__border-slate-200 company__shadow-sm">
                <div className="company__flex company__items-center company__gap-3 company__mb-4">
                  <div className="company__bg-green-100 company__rounded-lg company__p-3">
                    <MdPeople className="company__w-6 company__h-6 company__text-green-600" />
                  </div>
                  <div>
                    <h3 className="company__font-semibold company__text-lg company__text-slate-900">
                      {HERO.TARGETED_INVITES.TITLE}
                    </h3>
                    <p className="company__text-sm company__text-slate-600">
                      {HERO.TARGETED_INVITES.SUBTITLE}
                    </p>
                  </div>
                </div>

                <p className="company__text-sm company__text-slate-700 company__mb-4">
                  {HERO.TARGETED_INVITES.DESCRIPTION}
                </p>

                <div className="company__flex company__items-center company__gap-2 company__text-xs company__text-slate-600 company__mb-4">
                  <MdCheck className="company__w-4 company__h-4 company__text-green-600" />
                  <span>{HERO.TARGETED_INVITES.BULLET}</span>
                </div>

                <div className="company__bg-slate-50 company__border company__border-slate-200 company__rounded-lg company__p-4 company__mb-4">
                  <div className="company__flex company__items-center company__justify-between company__mb-2">
                    <span className="company__text-sm company__font-medium company__text-slate-900">
                      {HERO.TARGETED_INVITES.PENDING_TITLE}
                    </span>
                    <span className="company__text-2xl company__font-bold company__text-slate-900">
                      {employees.length}
                    </span>
                  </div>
                  {employees.length > 0 && (
                    <p className="company__text-xs company__text-slate-600">
                      {HERO.TARGETED_INVITES.PENDING_READY}
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleDeployToList}
                  disabled={
                    deploying ||
                    employees.length === 0 ||
                    !isConfigComplete ||
                    !hasBranchRequirement
                  }
                  type="success"
                  className="company__w-full"
                >
                  <MdRocketLaunch className="company__w-5 company__h-5" />
                  {deploying
                    ? HERO.TARGETED_INVITES.BUTTON.LOADING
                    : !isConfigComplete
                      ? HERO.TARGETED_INVITES.BUTTON.NEED_CONFIGURATION
                      : !hasBranchRequirement
                        ? HERO.TARGETED_INVITES.BUTTON.BRANCHES_REQUIRED
                        : employees.length === 0
                          ? HERO.TARGETED_INVITES.BUTTON.EMPTY
                          : t(
                              "DEPLOYMENT_MANAGER.HERO.TARGETED_INVITES.BUTTON.DEFAULT",
                              { count: employees.length },
                            )}
                </Button>
                {isCDGOrganization && !hasBranchRequirement && (
                  <p className="company__mt-3 company__text-xs company__text-amber-600">
                    {HERO.BRANCHES_REQUIRED}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="company__bg-white company__rounded-xl company__border company__border-slate-200 company__p-6">
        <div className="company__flex company__items-center company__gap-3 company__mb-6">
          <div className="company__bg-green-100 company__rounded-lg company__p-3">
            <MdPeople className="company__w-6 company__h-6 company__text-green-600" />
          </div>
          <div>
            <h3 className="company__text-xl company__font-semibold company__text-slate-900">
              {EMPLOYEE_SECTION.TITLE}
            </h3>
            <p className="company__text-sm company__text-slate-600">
              {EMPLOYEE_SECTION.SUBTITLE}
            </p>
          </div>
        </div>

        {employees.length === 0 && (
          <div className="company__bg-blue-50 company__border-2 company__border-blue-200 company__rounded-lg company__p-6 company__mb-6">
            <div className="company__flex company__items-start company__gap-4">
              <MdErrorOutline
                className="company__w-6 company__h-6 company__text-blue-600 company__flex-shrink-0"
                style={{ marginTop: "0.25rem" }}
              />
              <div>
                <h4 className="company__font-semibold company__text-blue-900 company__mb-2">
                  {EMPLOYEE_SECTION.GUIDE.TITLE}
                </h4>
                <ol className="company__space-y-2 company__text-sm company__text-blue-800">
                  {EMPLOYEE_SECTION.GUIDE.STEPS.map((step, index) => (
                    <li
                      key={step}
                      className="company__flex company__items-start company__gap-2"
                    >
                      <span className="company__font-bold">{index + 1}.</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        )}

        <div className="company__flex company__items-center company__justify-between company__mb-6">
          <div className="company__flex company__items-center company__gap-2">
            <MdOutlineDescription className="company__w-5 company__h-5 company__text-slate-600" />
            <p className="company__text-sm company__text-slate-700">
              {employees.length > 0 ? (
                <>
                  <strong>
                    {t("DEPLOYMENT_MANAGER.EMPLOYEE_SECTION.SUMMARY.COUNT", {
                      count: employees.length,
                    })}
                  </strong>{" "}
                  {EMPLOYEE_SECTION.SUMMARY.SUFFIX}
                </>
              ) : (
                EMPLOYEE_SECTION.SUMMARY.EMPTY
              )}
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={[
              ".csv",
              ".xlsx",
              ".xls",
              CONTENT_TYPES.CSV,
              CONTENT_TYPES.XLS,
              CONTENT_TYPES.XLSX,
              CONTENT_TYPES.SPREADSHEET,
            ].join(",")}
            onChange={handleFileChange}
            disabled={importing}
            style={{ display: "none" }}
          />
          <Button
            type="primary"
            disabled={importing}
            className="company__cursor-pointer"
            onClick={handleFileButtonClick}
          >
            <MdUpload className="company__w-5 company__h-5" />
            {importing
              ? EMPLOYEE_SECTION.IMPORT_BUTTON.LOADING
              : EMPLOYEE_SECTION.IMPORT_BUTTON.DEFAULT}
          </Button>
        </div>

        <div className="company__bg-slate-50 company__border company__border-slate-200 company__rounded-lg company__p-4 company__mb-6">
          <div className="company__flex company__gap-3">
            <MdErrorOutline
              className="company__w-5 company__h-5 company__text-slate-600 company__flex-shrink-0"
              style={{ marginTop: "0.125rem" }}
            />
            <div className="company__text-sm company__text-slate-700">
              <p className="company__font-medium company__mb-3">
                {CSV_HELP.TITLE}
              </p>

              <div className="company__mb-3">
                <p className="company__font-semibold company__text-slate-800 company__mb-1">
                  CSV :
                </p>
                <p className="company__mb-1">
                  <code className="company__bg-slate-100 company__px-2 company__py-1 company__rounded">
                    {CSV_HELP.FORMAT}
                  </code>
                </p>
                <p className="company__text-slate-600">
                  {CSV_HELP.EXAMPLE_LABEL}{" "}
                  <code className="company__bg-slate-100 company__px-2 company__py-1 company__rounded">
                    {CSV_HELP.EXAMPLE_VALUE}
                  </code>
                </p>
              </div>

              <div className="company__border-t company__border-slate-300 company__pt-3 company__mt-3">
                <p className="company__font-semibold company__text-slate-800 company__mb-1">
                  {CSV_HELP.EXCEL_TITLE || "Excel :"}
                </p>
                <p className="company__mb-2 company__text-slate-600">
                  {CSV_HELP.EXCEL_DESCRIPTION ||
                    "Your Excel file (.xlsx or .xls) must contain the following columns:"}
                </p>
                <p className="company__mb-2">
                  <code className="company__bg-slate-100 company__px-2 company__py-1 company__rounded company__font-semibold">
                    {CSV_HELP.EXCEL_COLUMNS || "Nom, Prénom, Email"}
                  </code>
                </p>
                <p className="company__text-xs company__text-slate-600 company__italic">
                  {CSV_HELP.EXCEL_NOTE ||
                    "The columns can be in any order, but must include these three fields."}
                </p>
              </div>
            </div>
          </div>
        </div>

        <Formik
          initialValues={employeeInitialValues}
          validationSchema={employeeValidationSchema}
          validateOnBlur={true}
          validateOnChange={false}
          onSubmit={handleAddEmployee}
        >
          {(formikProps) => {
            const { handleSubmit, isSubmitting, values } = formikProps;
            const formProps = { values, ...formikProps };

            return (
              <form
                onSubmit={handleSubmit}
                className="company__bg-slate-50 company__rounded-lg company__border company__border-slate-200 company__p-4 company__mb-6"
              >
                <h4 className="company__font-medium company__text-slate-900 company__mb-3">
                  {FORM_TEXT.TITLE}
                </h4>
                <div className="company__grid company__grid-cols-1 company__md-grid-cols-3 company__gap-3">
                  <Input
                    label={FORM_TEXT.FIELDS.EMAIL.LABEL}
                    placeholder={FORM_TEXT.FIELDS.EMAIL.PLACEHOLDER}
                    type="simple"
                    {...bindInputProps({
                      name: "email",
                      ...formProps,
                    })}
                    required
                    style={{ backgroundColor: COLORS.C_WHITE }}
                  />
                  <Input
                    label={FORM_TEXT.FIELDS.FIRSTNAME.LABEL}
                    placeholder={FORM_TEXT.FIELDS.FIRSTNAME.PLACEHOLDER}
                    type="simple"
                    {...bindInputProps({
                      name: "firstName",
                      ...formProps,
                    })}
                    required
                    style={{ backgroundColor: COLORS.C_WHITE }}
                  />
                  <Input
                    label={FORM_TEXT.FIELDS.LASTNAME.LABEL}
                    placeholder={FORM_TEXT.FIELDS.LASTNAME.PLACEHOLDER}
                    type="simple"
                    {...bindInputProps({
                      name: "lastName",
                      ...formProps,
                    })}
                    required
                    style={{ backgroundColor: COLORS.C_WHITE }}
                  />
                </div>
                <Button
                  htmlType="submit"
                  disabled={adding || isSubmitting}
                  type="success"
                  className="company__mt-3"
                >
                  <MdAdd className="company__w-5 company__h-5" />
                  {adding || isSubmitting
                    ? FORM_TEXT.SUBMIT.LOADING
                    : FORM_TEXT.SUBMIT.DEFAULT}
                </Button>
              </form>
            );
          }}
        </Formik>

        {employees.length > 0 && (
          <div className="company__overflow-x-auto company__border company__border-slate-200 company__rounded-lg">
            <table className="company__w-full">
              <thead className="company__bg-slate-50">
                <tr>
                  <th className="company__px-4 company__py-3 company__text-left company__text-xs company__font-medium company__text-slate-600 company__uppercase">
                    {TABLE_TEXT.HEADERS.EMAIL}
                  </th>
                  <th className="company__px-4 company__py-3 company__text-left company__text-xs company__font-medium company__text-slate-600 company__uppercase">
                    {TABLE_TEXT.HEADERS.FIRSTNAME}
                  </th>
                  <th className="company__px-4 company__py-3 company__text-left company__text-xs company__font-medium company__text-slate-600 company__uppercase">
                    {TABLE_TEXT.HEADERS.LASTNAME}
                  </th>
                  <th className="company__px-4 company__py-3 company__text-right company__text-xs company__font-medium company__text-slate-600 company__uppercase">
                    {TABLE_TEXT.HEADERS.ACTIONS}
                  </th>
                </tr>
              </thead>
              <tbody className="company__divide-y company__divide-slate-200 company__bg-white">
                {employees.slice(0, 10).map((employee) => (
                  <tr key={employee.id} className="company__hover-bg-slate-50">
                    <td className="company__px-4 company__py-3 company__text-sm company__text-slate-900">
                      {employee.email}
                    </td>
                    <td className="company__px-4 company__py-3 company__text-sm company__text-slate-700">
                      {employee.firstName || "-"}
                    </td>
                    <td className="company__px-4 company__py-3 company__text-sm company__text-slate-700">
                      {employee.lastName || "-"}
                    </td>
                    <td className="company__px-4 company__py-3 company__text-right company__flex company__items-center company__justify-end">
                      <MdOutlineDelete
                        size={16}
                        onClick={() => handleDelete(employee.id)}
                        color={COLORS.C_DANGER}
                        className="company__cursor-pointer"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {employees.length > 10 && (
              <div className="company__bg-slate-50 company__px-4 company__py-2 company__text-sm company__text-slate-600 company__text-center company__border-t company__border-slate-200">
                {t("DEPLOYMENT_MANAGER.TABLE.MORE_ROWS", {
                  count: employees.length - 10,
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <CSVMappingModal
        isOpen={showCSVMapping}
        onClose={() => {
          setShowCSVMapping(false);
          setCsvFile(null);
        }}
        onImport={handleCSVImport}
        csvFile={csvFile}
      />
    </div>
  );
}
